import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useToast from '../../../hooks/useToast';
import useScopes from '../../../hooks/useScopes';
import { apiErrorMessage } from '../../Akademy/SchoolMembers/schoolMembersApi';
import { getFeatureFlagTree } from '../../Dashboard/FeatureManagement/featureFlagsSlice';

// The thunks reject with the raw response body; apiErrorMessage expects an axios error.
export const thunkErrorMessage = (body, fallback) =>
  apiErrorMessage({ response: { data: body } }, fallback);

/** Depth-first search of the flag tree → `{ node, ancestors }` (ancestors root-down). */
export const findWithAncestors = (nodes, key, ancestors = []) => {
  for (const node of nodes || []) {
    if (node.key === key) return { node, ancestors };
    const found = findWithAncestors(node.children, key, [...ancestors, node]);
    if (found) return found;
  }
  return null;
};

/**
 * One copy of the flag tree for the whole Website page, so a write from any section
 * (Step 1, the features table) refreshes what every other section shows.
 *
 * → `{ canManage, email, tree, reload }`. `tree` is null until the first load; `reload()`
 * resolves with the fresh tree, or rejects with the raw response body.
 */
const useFlagTree = () => {
  const dispatch = useDispatch();
  const { ErrorToast } = useToast();
  const { access } = useScopes();

  const { roles, email } = useSelector(({ signInReducer }) => {
    const me = signInReducer.me && signInReducer.me.data;
    return {
      roles: (me && me.role) || [],
      email: me && me.email,
    };
  });

  // The flag routes need `featureFlags:readWrite`, which today only bccSuperAdmin holds
  // (via its `all` scope) — anyone else would get a 403 from every call.
  const canManage =
    roles.includes('bccSuperAdmin') ||
    access({ mode: 'allOf', scope: ['featureFlags:readWrite'] });

  const [tree, setTree] = useState(null);

  const reload = () =>
    dispatch(getFeatureFlagTree())
      .unwrap()
      .then((data) => {
        setTree(data || []);
        return data || [];
      });

  useEffect(() => {
    if (!canManage) return;
    reload().catch((body) =>
      ErrorToast(thunkErrorMessage(body, 'Could not load feature flags'))
    );
  }, [canManage]);

  return { canManage, email, tree, reload };
};

export default useFlagTree;
